import React, { useEffect, useState } from "react";
import numeral from "numeral";
import { Form } from "react-bootstrap";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import { IconButton, Tooltip } from "@mui/material";
import usePalette from "../../../../../../hooks/usePalette";
import CurrencyInput from "react-currency-input-field";

// Icons
import Icon from "@mdi/react";
import {
  mdiMinusCircle,
  mdiNoteTextOutline,
  mdiCircle,
  mdiPlusCircle,
} from "@mdi/js";

const TableBody = ({
  showColumn,
  handleCostSingleId,
  checkedCostId,
  handleRemoveComponent,
  data,
  handleOpenNoteSection,
  handleInitialCost,
  costValue,
  errors,
  viewColumns,
  handleShowProjectScopeForm,
}) => {
  const palette = usePalette();

  const { project, rejectedComponents } = useSelector((state) => ({
    project: state.certifyReducer.project.data,
    rejectedComponents: state.utilsReducer.rejectedComponents,
  }));

  const totalRequestedCostByApplicant = project?.financial_details?.reduce(
    (total, obj) => {
      return (total += parseFloat(obj.cost_required, 0));
    },
    0,
  );

  const totalRequestedCeilingByApplicant = project?.financial_details?.reduce(
    (total, obj) => {
      return (total += parseFloat(obj.ceiling, 0));
    },
    0,
  );

  const handleCalculateTotalEdited = () => {
    let rejectedCost = 0;

    if (!isEmpty(rejectedComponents)) {
      rejectedCost = rejectedComponents?.reduce(
        (acc, obj) => acc + parseFloat(obj?.cost_required),
        0,
      );
    }

    const totalEditCostRequired = data.reduce(
      (total, obj) => total + parseFloat(obj?.cost_required),
      0,
    );

    const totalEditCeiling = data
      ?.flatMap((obj) => obj?.ceiling_flow_details)
      ?.reduce((total, obj) => total + parseFloat(obj?.amount), 0);

    let totalValues = {
      totalCost: 0,
      totalCeiling: totalEditCeiling,
    };

    if (!isEmpty(costValue)) {
      const filteredData2 = data?.filter(
        (item2) => !costValue.some((item1) => item2?.id === item1?.id),
      );

      const sumInitialAmount = filteredData2?.reduce(
        (sum, item) => sum + parseFloat(item?.amount),
        0,
      );
      const sumEditedAmount = costValue.reduce(
        (sum, item) => sum + parseFloat(item?.cost_required),
        0,
      );
      const combinedSum = sumInitialAmount + sumEditedAmount;

      return {
        ...totalValues,
        totalCost: combinedSum - rejectedCost,
      };
    }

    return {
      ...totalValues,
      totalCost: totalEditCostRequired,
    };
  };

  const totalEditedValue = handleCalculateTotalEdited();

  const handleCertifyStages = (stages, status) => {
    let rejectedCost = 0;
    let rejectedCeiling = 0;

    if (!isEmpty(rejectedComponents) && project.stages === "4") {
      rejectedCost = rejectedComponents?.reduce(
        (acc, obj) => acc + parseFloat(obj.cost_required),
        0,
      );
      rejectedCeiling = rejectedComponents?.reduce(
        (acc, obj) => acc + parseFloat(obj.ceiling),
        0,
      );
    }

    const certifyStages = project?.financial_details
      ?.filter((obj) => obj.is_rejected === false)
      ?.flatMap((obj) => obj.financial_detail_stages);

    const findStages = certifyStages?.filter((obj) => {
      return (
        obj.stages === stages.toString() && obj.status === status.toString()
      );
    });

    const totalCost = findStages?.reduce(
      (total, obj) => total + parseFloat(obj.cost_required),
      0,
    );

    const totalCeiling = findStages?.reduce(
      (total, obj) => total + parseFloat(obj.ceiling),
      0,
    );

    return {
      total_cost_required: totalCost - rejectedCost,
      total_ceiling_required: totalCeiling - rejectedCeiling,
    };
  };

  const totalInitialRequested = handleCertifyStages(4, 2);
  const totalCertifiedByUpen = handleCertifyStages(5, 3);
  const totalCertifiedByJppn = handleCertifyStages(6, 3);

  return (
    <tbody style={{ fontWeight: 500 }}>
      {data?.map((component, idx) => {
        const certifyingAmount = component?.ceiling_flow_details?.reduce(
          (acc, item) => acc + parseFloat(item?.amount),
          0,
        );

        const findInitialCost = costValue.find(
          (item) => item?.id === component.id,
        );

        const initialCost = findInitialCost
          ? findInitialCost.cost_required
          : "";

        const handleFindInitialScope = (scopeId, stages, status) => {
          const findStages = component?.financial_detail_stages?.find(
            (obj) =>
              obj.stages === stages.toString() &&
              obj.status === status.toString(),
          );

          return findStages;
        };

        const findInitialInfo = handleFindInitialScope(component.id, 4, 2);

        const handleFindScope = (scopeId, stages, status) => {
          const findScope = project?.financial_details?.find(
            (obj) => obj.id === scopeId,
          );
          const findStages = findScope?.financial_detail_stages?.find(
            (obj) =>
              obj.stages === stages.toString() &&
              obj.status === status.toString(),
          );

          return findStages;
        };

        const findUpenStages = handleFindScope(component.id, 5, 3);
        const findJppnStages = handleFindScope(component.id, 6, 3);

        return (
          <tr key={component.id}>
            <td className='text-center'>{idx + 1}.</td>
            <td className='border-end-0 text-uppercase'>
              {component?.cost_description}
            </td>
            <td className='border-start-0 text-center'>
              <div>
                <Tooltip
                  title='Lhat catatan'
                  placement='top'
                  followCursor
                  arrow
                  className='p-0'
                >
                  <IconButton
                    onClick={() => handleOpenNoteSection(component)}
                    className='button-animated position-relative'
                    style={{ backgroundColor: "transparent" }}
                  >
                    <Icon
                      className=''
                      path={mdiNoteTextOutline}
                      size={0.8}
                      color={palette.primary}
                    />
                    {project.financial_details?.find(
                      (item) => item?.id === component?.id,
                    )?.financial_detail_notes.length !== 0 && (
                      <Icon
                        className='position-absolute'
                        path={mdiCircle}
                        size={0.45}
                        style={{ top: -2, right: -2 }}
                        color='#ef5350'
                      />
                    )}
                  </IconButton>
                </Tooltip>
              </div>
            </td>

            {viewColumns.dipohon.checked && (
              <>
                <td className='text-end text-truncate'>
                  {`${numeral(findInitialInfo?.cost_required).format(
                    "0,0.00",
                  )}`}
                </td>
                <td className='text-end text-truncate'>
                  {`${numeral(findInitialInfo?.ceiling).format("0,0.00")}`}
                </td>
              </>
            )}

            {/* UPEN SECTION  */}
            {viewColumns.upen.checked && (
              <>
                <td className='border-end-0'>
                  <div className='d-flex flex-row gap-4 justify-content-end text-truncate'>
                    {checkedCostId.includes(component.id) &&
                    !showColumn.jppn ? (
                      <React.Fragment>
                        <Form.Group className='w-100'>
                          <Form.Control
                            className={`text-end text-truncate`}
                            as={CurrencyInput}
                            min={0}
                            name='amount'
                            autoComplete='off'
                            decimalsLimit={2}
                            decimalScale={2}
                            value={initialCost}
                            onValueChange={(value) =>
                              handleInitialCost(
                                value,
                                findInitialCost,
                                certifyingAmount,
                              )
                            }
                            isInvalid={errors.some(
                              (item) => item === component.id,
                            )}
                          />
                          <Form.Control.Feedback type='invalid'>
                            Kos tidak boleh kurang dari siling
                          </Form.Control.Feedback>
                        </Form.Group>
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        {showColumn.jppn ? (
                          <>
                            {`${numeral(findUpenStages?.cost_required).format(
                              "0,0.00",
                            )}`}
                          </>
                        ) : (
                          <>{`${numeral(certifyingAmount).format("0,0.00")}`}</>
                        )}
                      </React.Fragment>
                    )}
                  </div>
                </td>

                <td className='text-end text-truncate'>
                  {showColumn.jppn ? (
                    <>
                      {`${numeral(findUpenStages?.ceiling).format("0,0.00")}`}
                    </>
                  ) : (
                    <>{`${numeral(certifyingAmount).format("0,0.00")}`}</>
                  )}
                </td>
              </>
            )}

            {/* JPPN SECTION  */}
            {showColumn.jppn && (
              <React.Fragment>
                {viewColumns.jppn.checked && (
                  <>
                    <td className='border-end-0'>
                      <div className='d-flex flex-row gap-4 justify-content-end text-truncate'>
                        {checkedCostId.includes(component.id) &&
                        !showColumn.cabinet ? (
                          <React.Fragment>
                            <Form.Group className='w-100'>
                              <Form.Control
                                className={`text-end text-truncate`}
                                as={CurrencyInput}
                                min={0}
                                name='amount'
                                autoComplete='off'
                                decimalsLimit={2}
                                value={initialCost}
                                onValueChange={(value) =>
                                  handleInitialCost(
                                    value,
                                    findInitialCost,
                                    certifyingAmount,
                                  )
                                }
                                isInvalid={errors.some(
                                  (item) => item === component.id,
                                )}
                              />
                              <Form.Control.Feedback type='invalid'>
                                Kos tidak boleh kurang dari siling
                              </Form.Control.Feedback>
                            </Form.Group>
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            {showColumn.cabinet ? (
                              <>
                                {`${numeral(
                                  findJppnStages?.cost_required,
                                ).format("0,0.00")}`}
                              </>
                            ) : (
                              <>
                                {`${numeral(certifyingAmount).format(
                                  "0,0.00",
                                )}`}
                              </>
                            )}
                          </React.Fragment>
                        )}
                      </div>
                    </td>
                    <td className='text-end text-truncate'>
                      {`${numeral(certifyingAmount).format("0,0.00")}`}
                    </td>
                  </>
                )}
              </React.Fragment>
            )}

            {/* CABINET SECTION  */}
            {showColumn.cabinet && (
              <React.Fragment>
                <td className='border-end-0'>
                  <div className='d-flex flex-row gap-4 justify-content-end text-truncate'>
                    {checkedCostId.includes(component.id) ? (
                      <React.Fragment>
                        <Form.Group className='w-100'>
                          <Form.Control
                            className={`text-end text-truncate`}
                            as={CurrencyInput}
                            min={0}
                            name='amount'
                            autoComplete='off'
                            decimalsLimit={2}
                            value={initialCost}
                            onValueChange={(value) =>
                              handleInitialCost(
                                value,
                                findInitialCost,
                                certifyingAmount,
                              )
                            }
                            isInvalid={errors.some(
                              (item) => item === component.id,
                            )}
                          />
                          <Form.Control.Feedback type='invalid'>
                            Kos tidak boleh kurang dari siling
                          </Form.Control.Feedback>
                        </Form.Group>
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        {`${numeral(certifyingAmount).format("0,0.00")}`}
                      </React.Fragment>
                    )}
                  </div>
                </td>

                <td className='text-end text-truncate'>
                  {`${numeral(certifyingAmount).format("0,0.00")}`}
                </td>
              </React.Fragment>
            )}

            <td>
              {project.stages === "4" && (
                <div className='d-flex justify-content-center'>
                  <Tooltip title='Tekan untuk tidak peraku' arrow followCursor>
                    <span>
                      <IconButton
                        className='p-0'
                        onClick={() => handleRemoveComponent(component)}
                        color='error'
                        disabled={data.length === 1}
                      >
                        <Icon path={mdiMinusCircle} size={0.8} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              )}
            </td>
          </tr>
        );
      })}
      {/* <tr>
        <td colSpan={3} className='text-center border-end-0'>
          <Tooltip
            title='Tambah Skop'
            placement='top'
            followCursor
            arrow
            className='p-0'
          >
            <IconButton
              className='button-animated position-relative'
              style={{ backgroundColor: "transparent" }}
              onClick={handleShowProjectScopeForm}
            >
              <Icon
                className=''
                path={mdiPlusCircle}
                size={1}
                color={palette.primary}
              />
            </IconButton>
          </Tooltip>
        </td>
        <td colSpan={9} className='text-center border-start-0' />
      </tr> */}
      <tr style={{ borderTop: "double" }}>
        <td
          colSpan={3}
          className='text-end text-uppercase'
          style={{ fontWeight: 700 }}
        >
          Jumlah
        </td>

        {viewColumns.dipohon.checked && (
          <>
            <td
              className='text-end text-uppercase'
              style={{ fontWeight: 700, width: "13%" }}
            >
              {numeral(totalInitialRequested.total_cost_required).format(
                "0,0.00",
              )}
            </td>
            <td
              className='text-end text-uppercase'
              style={{ fontWeight: 700, width: "13%" }}
            >
              {numeral(totalInitialRequested.total_ceiling_required).format(
                "0,0.00",
              )}
            </td>
          </>
        )}

        {/* UPEN COLUMN */}
        {viewColumns.upen.checked && (
          <>
            <td
              className='text-end text-uppercase border-end-0'
              style={{ fontWeight: 700, width: "13%" }}
            >
              {showColumn.jppn ? (
                <>
                  {numeral(totalCertifiedByUpen.total_cost_required).format(
                    "0,0.00",
                  )}
                </>
              ) : (
                <>{numeral(totalEditedValue.totalCost).format("0,0.00")}</>
              )}
            </td>

            <td
              className='text-end text-uppercase'
              style={{ fontWeight: 700, width: "13%" }}
            >
              {showColumn.jppn ? (
                <>
                  {numeral(totalCertifiedByUpen.total_ceiling_required).format(
                    "0,0.00",
                  )}
                </>
              ) : (
                <>{numeral(totalEditedValue.totalCeiling).format("0,0.00")}</>
              )}
            </td>
          </>
        )}

        {/* JPPN COLUMN */}
        {showColumn.jppn && (
          <>
            {viewColumns.jppn.checked && (
              <>
                <td
                  className='text-end text-uppercase border-end-0'
                  style={{ fontWeight: 700, width: "13%" }}
                >
                  {showColumn.cabinet ? (
                    <>
                      {numeral(totalCertifiedByJppn.total_cost_required).format(
                        "0,0.00",
                      )}
                    </>
                  ) : (
                    <>{numeral(totalEditedValue.totalCost).format("0,0.00")}</>
                  )}
                </td>

                <td
                  className='text-end text-uppercase'
                  style={{ fontWeight: 700, width: "13%" }}
                >
                  {showColumn.cabinet ? (
                    <>
                      {numeral(
                        totalCertifiedByJppn.total_ceiling_required,
                      ).format("0,0.00")}
                    </>
                  ) : (
                    <>
                      {numeral(totalEditedValue.totalCeiling).format("0,0.00")}
                    </>
                  )}
                </td>
              </>
            )}
          </>
        )}

        {/* CABINET COLUMN */}
        {showColumn.cabinet && (
          <>
            <td
              className='text-end text-uppercase border-end-0'
              style={{ fontWeight: 700, width: "13%" }}
            >
              <>{numeral(totalEditedValue.totalCost).format("0,0.00")}</>
            </td>

            <td
              className='text-end text-uppercase'
              style={{ fontWeight: 700, width: "13%" }}
            >
              <>{numeral(totalEditedValue.totalCeiling).format("0,0.00")}</>
            </td>
          </>
        )}

        {/* Last column */}
        <td style={{ width: "3%" }} />
      </tr>
    </tbody>
  );
};

export default TableBody;
